import type { PropsWithChildren } from 'react'
import { createContext, useContext, useState, useEffect } from 'react'

import { useTranslation } from 'react-i18next'

import { useSongManager } from 'hooks/useSongManager'

type SongContextProps = {
  currentSongTitle: string
  intermission: boolean
  queueFull: boolean
  selectedSongId: null | number
  coolDowns: Array<number>
  setCoolDowns: (value: Array<number>) => void
  setCurrentSongTitle: (data: string) => void
  setIntermission: (value: boolean) => void
  setQueueFull: (value: boolean) => void
  setSelectedSongId: (value: null | number) => void
}

const noop = (): void => {}

export const SongContext = createContext<SongContextProps>({
  currentSongTitle: '-',
  intermission: false,
  queueFull: false,
  selectedSongId: null,
  coolDowns: [],
  setCoolDowns: noop,
  setSelectedSongId: noop,
  setCurrentSongTitle: noop,
  setIntermission: noop,
  setQueueFull: noop,
})

export const SongProvider = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation()
  const [currentSongTitle, setCurrentSongTitle] = useState<string>(t('no-song'))
  const [intermission, setIntermission] = useState<boolean>(false)
  const [queueFull, setQueueFull] = useState<boolean>(false)
  const [coolDowns, setCoolDowns] = useState<Array<number>>([])
  const { getQueue, getStatus } = useSongManager()
  const [selectedSongId, setSelectedSongId] = useState<null | number>(null)

  useEffect(() => {
    getQueue()
    getStatus()
  }, [])

  return (
    <SongContext.Provider
      value={{
        currentSongTitle,
        intermission,
        queueFull,
        selectedSongId,
        coolDowns,
        setCoolDowns,
        setCurrentSongTitle,
        setIntermission,
        setQueueFull,
        setSelectedSongId,
      }}
    >
      {children}
    </SongContext.Provider>
  )
}

export function useSongContext() {
  const context = useContext<SongContextProps>(SongContext)
  if (!context) {
    throw new Error('useSongContext must be used within a AppProvider')
  }
  return context
}
